import React from "react";
import { Row, Col } from "react-bootstrap";
import { RBSModal } from "../../base/rbs_components/RBSModal.tsx";
import { RBSTextField } from "../../base/rbs_components/RBSTextField.tsx";
import { RBSDropdown } from "../../base/rbs_components/RBSDropdown.tsx";
import type {
    PortfolioRowInput,
    PortfolioRow,
    TimeSeries
} from "../../base/ui_general/GeneralInterfaces.tsx";
import { ValidationSchemes, TextfieldInputTypes, LabelTextMessageTypes } from "../../base/Settings.tsx";
import { getLabelTextMessage } from "../../base/utils/GeneralUtilities.tsx";
import type { InputStateControl } from "../../base/utils/InputStateControl.tsx";
import type { UserLanguages } from "../../base/Settings.tsx";
import { AmortizationTypeEnum } from "../../base/Settings.tsx";


//TODO: There are places where the multi-language support is missing

/**
 * Props for the PortfolioRowDetailModal component.
 */
interface PortfolioRowDetailModalProps {
    /** Whether the modal is visible. */
    show: boolean;
    /** The portfolio row to edit (if any). */
    portfolioRow?: PortfolioRow;
    /** Callback when submitting the modal form. */
    onSubmit: (portfolioRowInput: PortfolioRowInput) => void;
    /** Callback when closing the modal. */
    onClose: () => void;
    /** Whether the current form state is valid. */
    isValid: boolean;
    /** State control for input fields. */
    inputStateControl: InputStateControl;
    /** The user's selected language. */
    user_language: UserLanguages;
    /** List of available time series for dropdowns. */
    time_series: Array<TimeSeries>;
    /** The ID of the current portfolio. */
    currentPortfolioId: string;
}


export const PortfolioRowDetailModal: React.FC<PortfolioRowDetailModalProps> = (props) => {
    const { portfolioRow, inputStateControl, user_language, time_series } = props;

    const prefill = (key: keyof PortfolioRowInput): string => {
        if (portfolioRow && portfolioRow[key] !== undefined && portfolioRow[key] !== null) {
            return String(portfolioRow[key]);
        }
        switch (key) {
            case "maturity": return "1.0";
            case "pd": return "0.02";
            case "lgd": return "0.45";
            case "initial_lgd": return "0.45";
            case "asset_correlation": return "0.0";
            case "initial_notional": return "1000000";
            case "spread": return "0.03";
            case "cpr": return "0.02";
            case "recovery_lag": return "1";
            case "amortization_type": return AmortizationTypeEnum.LINEAR;
            default: return "";
        }
    };

    const headerTitle = portfolioRow
        ? "Update Portfolio Row"  //TODO: Add language support
        : "Create New Portfolio Row";

    return (
        <RBSModal
            show={props.show}
            header_title={headerTitle}
            show_close_button
            onClose={props.onClose}
            primary_button_label={getLabelTextMessage(LabelTextMessageTypes.SUBMIT, user_language)}
            secondary_button_label={getLabelTextMessage(LabelTextMessageTypes.CANCEL, user_language)}
            onClickSecondary={props.onClose}
            primary_button_disabled={!props.isValid}
            size="xl"
            onClickPrimary={() => {
                const input: PortfolioRowInput = {
                    portfolio_id: props.currentPortfolioId,
                    maturity: Number(inputStateControl.getStringValueForKey("maturity")),
                    pd: Number(inputStateControl.getStringValueForKey("pd")),
                    lgd: Number(inputStateControl.getStringValueForKey("lgd")),
                    initial_lgd: Number(inputStateControl.getStringValueForKey("initial_lgd")),
                    asset_correlation: Number(inputStateControl.getStringValueForKey("asset_correlation")),
                    initial_notional: Number(inputStateControl.getStringValueForKey("initial_notional")),
                    spread: Number(inputStateControl.getStringValueForKey("spread")),
                    cpr: Number(inputStateControl.getStringValueForKey("cpr")),
                    recovery_lag: Number(inputStateControl.getStringValueForKey("recovery_lag")),
                    amortization_type: inputStateControl.getStringValueForKey("amortization_type") as AmortizationTypeEnum,
                    base_curve_id: inputStateControl.getStringValueForKey("base_curve_id") || undefined,
                };

                props.onSubmit(input);
                props.onClose();
            }}
        >

            <Row>
                <Col md={4}>
                    <RBSTextField
                        label="Maturity (years)"
                        input_key="maturity"
                        prefill={prefill("maturity")}
                        validation_schemes={[ValidationSchemes.REQUIRED, ValidationSchemes.NUMBER, ValidationSchemes.NON_NEGATIVE]}
                        input_state_control={inputStateControl}
                        type={TextfieldInputTypes.NUMBER}
                    />
                </Col>
                <Col md={4}>
                    <RBSTextField
                        label="Probability of Default (PD)"
                        input_key="pd"
                        prefill={prefill("pd")}
                        validation_schemes={[ValidationSchemes.REQUIRED, ValidationSchemes.NUMBER, ValidationSchemes.NON_NEGATIVE]}
                        input_state_control={inputStateControl}
                        type={TextfieldInputTypes.NUMBER}
                    />
                </Col>
                <Col md={4}>
                    <RBSTextField
                        label="Loss Given Default (LGD)"
                        input_key="lgd"
                        prefill={prefill("lgd")}
                        validation_schemes={[ValidationSchemes.REQUIRED, ValidationSchemes.NUMBER]}
                        input_state_control={inputStateControl}
                        type={TextfieldInputTypes.NUMBER}
                    />
                </Col>
            </Row>


            <Row>
                <Col md={4}>
                    <RBSTextField
                        label="Initial LGD"
                        input_key="initial_lgd"
                        prefill={prefill("initial_lgd")}
                        validation_schemes={[ValidationSchemes.REQUIRED, ValidationSchemes.NUMBER]}
                        input_state_control={inputStateControl}
                        type={TextfieldInputTypes.NUMBER}
                    />
                </Col>
                <Col md={4}>
                    <RBSTextField
                        label="Asset Correlation"
                        input_key="asset_correlation"
                        prefill={prefill("asset_correlation")}
                        validation_schemes={[ValidationSchemes.REQUIRED, ValidationSchemes.NUMBER, ValidationSchemes.NON_NEGATIVE]}
                        input_state_control={inputStateControl}
                        type={TextfieldInputTypes.NUMBER}
                    />
                </Col>
                <Col md={4}>
                    <RBSTextField
                        label="Initial Notional (€)"
                        input_key="initial_notional"
                        prefill={prefill("initial_notional")}
                        validation_schemes={[ValidationSchemes.REQUIRED, ValidationSchemes.NUMBER, ValidationSchemes.NON_NEGATIVE]}
                        input_state_control={inputStateControl}
                        type={TextfieldInputTypes.NUMBER}
                    />
                </Col>
            </Row>


            <Row>
                <Col md={4}>
                    <RBSTextField
                        label="Spread"
                        input_key="spread"
                        prefill={prefill("spread")}
                        validation_schemes={[ValidationSchemes.REQUIRED, ValidationSchemes.NUMBER]}
                        input_state_control={inputStateControl}
                        type={TextfieldInputTypes.NUMBER}
                    />
                </Col>
                <Col md={4}>
                    <RBSTextField
                        label="CPR (Constant Prepayment Rate)"
                        input_key="cpr"
                        prefill={prefill("cpr")}
                        validation_schemes={[ValidationSchemes.REQUIRED, ValidationSchemes.NUMBER, ValidationSchemes.NON_NEGATIVE]}
                        input_state_control={inputStateControl}
                        type={TextfieldInputTypes.NUMBER}
                    />
                </Col>
                <Col md={4}>
                    <RBSTextField
                        label="Recovery Lag (years)"
                        input_key="recovery_lag"
                        prefill={prefill("recovery_lag")}
                        validation_schemes={[ValidationSchemes.REQUIRED, ValidationSchemes.NUMBER, ValidationSchemes.NON_NEGATIVE]}
                        input_state_control={inputStateControl}
                        type={TextfieldInputTypes.NUMBER}
                    />
                </Col>
            </Row>


            <Row>
                <Col md={6}>
                    <RBSDropdown
                        label="Amortization Type"
                        input_key="amortization_type"
                        prefill={prefill("amortization_type")}
                        items={[
                            { label: "Linear", value: AmortizationTypeEnum.LINEAR },
                            { label: "Annuity", value: AmortizationTypeEnum.ANNUITY },
                            { label: "Bullet", value: AmortizationTypeEnum.BULLET },
                        ]}
                        input_state_control={inputStateControl}
                        validation_schemes={[ValidationSchemes.REQUIRED]}
                    />
                </Col>
                <Col md={6}>
                    <RBSDropdown
                        label="Base Curve"
                        input_key="base_curve_id"
                        prefill={prefill("base_curve_id")}
                        items={[
                            { label: "---", value: "" },
                            ...(time_series ? time_series.map(ts => ({ label: ts.name, value: ts.id })) : []),
                        ]}
                        input_state_control={inputStateControl}
                        validation_schemes={[]}
                    />
                </Col>
            </Row>
        </RBSModal>
    );
};
